/**
 * Some utility functions for calling Google Analytics
 */
import {__InternalGetUserAttributes, PermissionsInfo} from "@khan/shared-core";

import * as AnalyticsCookies from "./analytics-cookies.ts";
import {
    sendAnEventThatIsNotChildDirectedToAdvertisers,
    sendAnEventToGA4,
    type SignupRole,
} from "./gtm.ts";

const TRACKER_1 = "UA-6742635-1";
const TRACKER_12 = "UA-6742635-12";

// The name and scope of Google Analytics custom dimensions is now
// stored on the GA servers, and does not need to be passed up from
// the client. We only need to send index and value information.
// https://developers.google.com/analytics/devguides/collection/analyticsjs/custom-dims-mets
const Dimensions = {
    JUST_SIGNED_UP: 1 as const,
    JUST_LOGGED_IN: 2 as const,
    IS_NEW: 3 as const,
    IS_PHANTOM: 4 as const,
    IS_LOGGED_IN: 5 as const,
    SIGNUP_ROLE: 6 as const,
} as const;

const _getDimensions = (
    firstLogin: boolean,
    returningLogin: boolean,
    justLoggedOut: boolean,
    signupRole?: SignupRole | null,
): Partial<
    Record<
        (typeof Dimensions)[keyof typeof Dimensions],
        "true" | SignupRole | null | undefined
    >
> => {
    const dimensions: Partial<
        Record<
            (typeof Dimensions)[keyof typeof Dimensions],
            "true" | SignupRole | null | undefined
        >
    > = {};

    /**
     * The first 5 are potentially useful on their own, but are mostly
     * intended to help answer the question "what was the type of this
     * user when they started the session?"
     *
     * This is not trivial because session scope custom dimensions
     * always overwrite; the last value in a session is applied to the
     * entire session. To get around this, we use a combination of five
     * booleans that can be safely set to 'true' many times per session.
     *
     * New unregistered user:
     *   IS_NEW &&
     *   !JUST_LOGGED_IN     Rules out returning registered users who
     *                       don't start the session logged in.
     *
     * Returning unregistered user:
     *   !IS_NEW &&
     *   IS_PHANTOM &&       Rules out returning registered users who
     *                       start the session logged in.
     *   !JUST_LOGGED_IN     Rules out returning registered users who
     *                       don't start the session logged in.
     *
     * Returning registered user:
     *   IS_LOGGED_IN &&
     *   !JUST_SIGNED_UP     Rules out new/returning unregistered users
     *                       who signed up during the session.
     *
     * Note that we pass the string 'true' instead of a boolean,
     * because GA converts booleans to the strings '1' and '0'
     * which are more cumbersome to use in the web interface.
     */

    if (firstLogin) {
        // [session scope] Did the user sign up this session?
        dimensions[Dimensions.JUST_SIGNED_UP] = "true";
    }

    if (returningLogin) {
        // [session scope] Did the (returning registered) user login this session?
        dimensions[Dimensions.JUST_LOGGED_IN] = "true";
    }

    if (!justLoggedOut) {
        // user_data is cleared on logout

        if (!PermissionsInfo.hasProfile) {
            // [session scope] Has the unregistered user not taken any learning actions yet?
            dimensions[Dimensions.IS_NEW] = "true";
        } else if (PermissionsInfo.isPhantom) {
            // [session scope] Has the unregistered user taken any learning actions
            // and acquired a phantom user profile.
            dimensions[Dimensions.IS_PHANTOM] = "true";
        } else {
            // [session scope] Is the user currently logged in?
            dimensions[Dimensions.IS_LOGGED_IN] = "true";
        }
    }

    if (signupRole) {
        // [session scope] What role did the user indicate on signup?
        dimensions[Dimensions.SIGNUP_ROLE] = signupRole;
    }

    return dimensions;
};

const _getSignupRole = (): SignupRole | null | undefined => {
    const userAttributes = __InternalGetUserAttributes();
    if (!userAttributes) {
        return null;
    }

    if (userAttributes.isTeacher) {
        return "Teacher";
    }
    if (userAttributes.isParent) {
        return "Parent";
    }
    return "Student";
};

// This is a source of truth for the Google Analytics events that
// we expect to send from the web client.
// TODO(kairui): consolidate existing, scattered calls to GA into
// this object.
type GAEvent = "videoStart" | "SAT-CollegeBoard-Link";

const _createTracker = (id: string) => {
    const ga = GoogleAnalytics.ga;
    ga("create", id, {
        cookieDomain: ".khanacademy.org",
    });
};

/**
 * Google Analytics and Google Tag Manager events to trigger
 * when a user logs in for the first time.
 */
const _firstLoginEvents = (signupRole?: SignupRole | null) => {
    sendAnEventThatIsNotChildDirectedToAdvertisers({
        event: "signupComplete",
        role: signupRole,
    });

    GoogleAnalytics.sendToGA("event", "Accounts", "Signup", signupRole);
};

/**
 * Google Analytics and Google Tag Manager events to trigger
 * whenever a user logs in.
 */
const _returningLoginEvents = (signupRole?: SignupRole | null) => {
    sendAnEventThatIsNotChildDirectedToAdvertisers({
        event: "returningLogin",
        role: signupRole,
    });

    GoogleAnalytics.sendToGA("event", "Accounts", "ReturningLogin", signupRole);
};

/**
 * Initializes Google Analytics and sends initial Google Analytics
 * and Google Tag Manager events.
 */
export const init = () => {
    const userAttributes = __InternalGetUserAttributes();
    const kaid = userAttributes?.kaid;

    const firstLogin = kaid ? AnalyticsCookies.firstLogin(kaid) : false;
    const returningLogin = kaid ? AnalyticsCookies.returningLogin(kaid) : false;
    const justLoggedOut = AnalyticsCookies.justLoggedOut();
    const signupRole = GoogleAnalytics._getSignupRole();

    // TODO: add a check to see that 'ga' exists
    const ga = GoogleAnalytics.ga;

    // We create two trackers to gain more views in GA.
    GoogleAnalytics._createTracker(TRACKER_1);
    GoogleAnalytics._createTracker(TRACKER_12);

    ga("set", "transport", "beacon");
    ga("set", "anonymizeIp", true);

    const dimensions = GoogleAnalytics._getDimensions(
        firstLogin,
        returningLogin,
        justLoggedOut,
        signupRole,
    );

    for (const [key, value] of Object.entries(dimensions)) {
        ga("set", `dimension${key}`, value);
    }

    if (justLoggedOut) {
        // NOTE(kevinb): I don't think this is actually ending the session
        // since gaUserId is undefined if a user just logged out.
        ga("set", "sessionControl", "end");
    }

    ga("send", "pageview");
    ga("secondTracker.send", "pageview");

    if (firstLogin) {
        GoogleAnalytics._firstLoginEvents(signupRole);
    }

    if (returningLogin) {
        GoogleAnalytics._returningLoginEvents(signupRole);
    }
};

const _sendToGa = (...args: Array<any>): Array<Promise<void>> => {
    const trackerOne = new Promise(
        (resolve: unknown, reject: unknown): void => {
            GoogleAnalytics.ga("send", ...args, {hitCallback: resolve});
        },
    );
    const trackerTwo = new Promise(
        (resolve: unknown, reject: unknown): void => {
            GoogleAnalytics.ga("secondTracker.send", ...args, {
                hitCallback: resolve,
            });
        },
    );
    // @ts-expect-error - TS2322 - Type 'Promise<unknown>' is not assignable to type 'Promise<void>'. | TS2322 - Type 'Promise<unknown>' is not assignable to type 'Promise<void>'.
    return [trackerOne, trackerTwo];
};

/**
 * Sends event-type hits to Google Analytics, given an event type
 * and associated data contextualizing the event.
 *
 * @param {String} The event that should be sent to GA. Should be
 *                 a member of the list of allowable events defined
 *                 in this function.
 * @param {Object} Data about the event.
 *
 */
const sendToGA = (...args: Array<any>) => {
    if (!GoogleAnalytics._gaExists()) {
        return Promise.resolve([]);
    }
    return Promise.all(_sendToGa(...args));
};

const _sendNonInteractionEventToGA = (
    ...args: Array<any>
): Array<Promise<void>> => {
    const trackerOne = new Promise(
        (resolve: unknown, reject: unknown): void => {
            GoogleAnalytics.ga("send", ...args, {
                nonInteraction: true,
                hitCallback: resolve,
            });
        },
    );
    const trackerTwo = new Promise(
        (resolve: unknown, reject: unknown): void => {
            GoogleAnalytics.ga("secondTracker.send", ...args, {
                nonInteraction: true,
                hitCallback: resolve,
            });
        },
    );
    // @ts-expect-error - TS2322 - Type 'Promise<unknown>' is not assignable to type 'Promise<void>'. | TS2322 - Type 'Promise<unknown>' is not assignable to type 'Promise<void>'.
    return [trackerOne, trackerTwo];
};

// This method still executes ga.("send"), like the above
// method, but it has an extra property on the fields
// object for noninteraction, for use cases that require that item
const sendNonInteractionEventToGA = (...args: Array<any>) => {
    if (!GoogleAnalytics._gaExists()) {
        return Promise.resolve([]);
    }
    return Promise.all(_sendNonInteractionEventToGA(...args));
};

const sendEventToGA = (event: GAEvent, data: any): void => {
    switch (event) {
        case "videoStart":
            GoogleAnalytics.sendToGA(
                "event",
                "Learning-Action",
                "Video-Start",
                data.videoSlug,
            );
            sendAnEventToGA4({
                event: "learningAction",
                actionType: "Video-Start",
                slug: data.videoSlug,
            });
            break;
        case "SAT-CollegeBoard-Link":
            GoogleAnalytics.sendToGA("event", "Click", "SAT-CollegeBoard-Link");
            sendAnEventToGA4({
                event: "SAT-CollegeBoard-Link",
            });
            break;
    }
};

/**
 * Inspect Google Analytics' client-side tracking object to get the
 * referrer for a particular user session.
 */
const getGAReferrer = () => {
    if (!GoogleAnalytics._gaExists()) {
        return "";
    }
    let referrer = "";
    GoogleAnalytics.ga((tracker) => (referrer = tracker.get("referrer")));
    return referrer;
};

/**
 * Check if window.ga exists.
 */
// @ts-expect-error - TS2339 - Property 'ga' does not exist on type 'Window & typeof globalThis'.
const _gaExists = (): boolean => typeof window === "undefined" || window.ga;

const ga = (...args: Array<any>) => {
    if (!GoogleAnalytics._gaExists()) {
        return;
    }
    // @ts-expect-error - TS2339 - Property 'ga' does not exist on type 'Window & typeof globalThis'.
    window.ga(...args);
};

const GoogleAnalytics = {
    init,
    sendToGA,
    sendNonInteractionEventToGA,
    sendEventToGA,
    getGAReferrer,
    ga,

    // Exported for testing purposes only.
    _getDimensions,
    _getSignupRole,
    _createTracker,
    _firstLoginEvents,
    _returningLoginEvents,
    _gaExists,
    Dimensions,
} as const;

export default GoogleAnalytics;
